import * as React from 'react';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import { Button, Card, CardContent, CardHeader, CardTitle, DropdownSelect, EmptyState, Input, Badge, PageHeader, SkeletonText, Tabs, useConfirm } from '@/components/ui';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Paperclip, Trash2, Download } from 'lucide-react';

type Category = { id: string; name: string; description?: string; position: number; folders: Folder[] };
type Folder = { id: string; name: string; visibility: string; categoryId: string; _count: { articles: number } };
type Article = { id: string; title: string; status: string; views: number; tags: string[]; folderId?: string; publishedAt?: string; folder: { name: string; category: { name: string } }; author?: { firstName: string; lastName: string } };
type KbAttachment = { id: string; fileName: string; fileSize: number; contentType: string; createdAt: string };

export function KbAdminPage() {
  const { t } = useI18n();
  const { confirm } = useConfirm();
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [articles, setArticles] = React.useState<Article[]>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState<'articles' | 'structure'>('articles');

  const [showEditor, setShowEditor] = React.useState(false);
  const [editingArticle, setEditingArticle] = React.useState<Article | null>(null);
  const [form, setForm] = React.useState({ title: '', content: '', folderId: '', status: 'draft', tags: '' });
  const [attachments, setAttachments] = React.useState<KbAttachment[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [cats, arts] = await Promise.all([
        api.get<Category[]>('/admin/kb/categories'),
        api.get<Article[]>('/admin/kb/articles'),
      ]);
      setCategories(Array.isArray(cats) ? cats : []);
      setArticles(Array.isArray(arts) ? arts : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const filteredArticles = selectedFolder
    ? articles.filter((a) => a.folderId === selectedFolder)
    : articles;

  async function handleSaveArticle() {
    const payload = {
      title: form.title,
      content: form.content,
      folderId: form.folderId,
      status: form.status,
      tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
    };

    if (editingArticle) {
      await api.put(`/admin/kb/articles/${editingArticle.id}`, payload);
    } else {
      await api.post('/admin/kb/articles', payload);
    }
    setShowEditor(false);
    setEditingArticle(null);
    setForm({ title: '', content: '', folderId: '', status: 'draft', tags: '' });
    await loadData();
  }

  async function handleDelete(id: string) {
    if (!await confirm({ title: t('dir.kb.confirmDeleteTitle'), description: t('dir.kb.confirmDeleteDescription'), confirmLabel: t('dir.kb.confirmDeleteLabel') })) return;
    await api.delete(`/admin/kb/articles/${id}`);
    await loadData();
  }

  async function handleTogglePublish(article: Article) {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    await api.put(`/admin/kb/articles/${article.id}`, { status: newStatus });
    await loadData();
  }

  async function openEditor(article?: Article) {
    if (article) {
      setEditingArticle(article);
      // Fetch full article content from API
      try {
        const full = await api.get<any>(`/admin/kb/articles/${article.id}`);
        setForm({
          title: full.title || article.title,
          content: full.content || '',
          folderId: full.folderId || article.folderId || '',
          status: full.status || article.status,
          tags: (full.tags || article.tags)?.join(', ') || '',
        });
      } catch {
        setForm({
          title: article.title,
          content: '',
          folderId: article.folderId || '',
          status: article.status,
          tags: article.tags?.join(', ') || '',
        });
      }
      loadAttachments(article.id);
    } else {
      setEditingArticle(null);
      setForm({ title: '', content: '', folderId: '', status: 'draft', tags: '' });
      setAttachments([]);
    }
    setShowEditor(true);
  }

  async function loadAttachments(articleId: string) {
    try {
      const list = await api.get<KbAttachment[]>(`/admin/kb/articles/${articleId}/attachments`);
      setAttachments(Array.isArray(list) ? list : []);
    } catch { setAttachments([]); }
  }

  async function handleUploadAttachment(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editingArticle) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.upload(`/admin/kb/articles/${editingArticle.id}/attachments`, fd);
      await loadAttachments(editingArticle.id);
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDeleteAttachment(attachmentId: string) {
    if (!await confirm({ title: t('dir.kb.confirmRemoveTitle'), description: t('dir.kb.confirmRemoveDescription'), confirmLabel: t('dir.kb.confirmRemoveLabel') })) return;
    try {
      await api.delete(`/admin/kb/attachments/${attachmentId}`);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) { console.error(err); }
  }

  async function handleDownloadAttachment(attachmentId: string) {
    try {
      const meta = await api.get<{ url: string; fileName: string }>(`/admin/kb/attachments/${attachmentId}/url`);
      window.open(meta.url, '_blank');
    } catch (err) { console.error(err); }
  }

  function formatFileSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Card><CardContent className="p-6"><SkeletonText lines={2} /></CardContent></Card>
        <Card><CardContent className="p-6"><SkeletonText lines={8} /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: t('dir.kb.title') }]}
        title={t('dir.kb.title')}
        description={t('dir.kb.description', { articles: articles.length, folders: categories.reduce((sum, c) => sum + c.folders.length, 0) })}
        actions={<Button onClick={() => openEditor()}>{t('dir.kb.newArticle')}</Button>}
      />

      <Tabs
        aria-label={t('dir.kb.sectionsAria')}
        value={tab}
        onChange={(value) => setTab(value as 'articles' | 'structure')}
        items={[
          { value: 'articles', label: t('dir.kb.tabArticles') },
          { value: 'structure', label: t('dir.kb.tabStructure') },
        ]}
      />

      {tab === 'articles' && (
        <div className="flex gap-6">
          <nav className="w-56 shrink-0 space-y-1" aria-label={t('dir.kb.folderFilterAria')}>
            <button
              onClick={() => setSelectedFolder(null)}
              className={`w-full rounded px-3 py-2 text-left text-sm ${!selectedFolder ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-muted hover:bg-surface-hover'}`}
            >
              {t('dir.kb.allArticles', { count: articles.length })}
            </button>
            {categories.map((cat) => (
              <div key={cat.id} className="mt-3">
                <p className="px-3 text-xs font-semibold uppercase text-ink-subtle">{cat.name}</p>
                {cat.folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => setSelectedFolder(folder.id)}
                    className={`w-full rounded px-3 py-1.5 text-left text-sm ${selectedFolder === folder.id ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-muted hover:bg-surface-hover'}`}
                  >
                    {folder.name} <span className="text-xs text-ink-subtle">({folder._count.articles})</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div className="flex-1 space-y-2">
            {filteredArticles.length === 0 ? (
              <Card><CardContent className="p-0"><EmptyState title={t('dir.kb.writeFirst')} description={t('dir.kb.writeFirstDescription')} /></CardContent></Card>
            ) : filteredArticles.map((article) => (
              <Card key={article.id} className="transition hover:border-brand-200">
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <h3 className="font-medium text-ink">{article.title}</h3>
                    <p className="mt-0.5 text-xs text-ink-subtle">
                      {article.folder?.category?.name} / {article.folder?.name}
                      {article.author && <> &middot; {t('dir.kb.byAuthor', { name: `${article.author.firstName} ${article.author.lastName}` })}</>}
                      &middot; {t('dir.kb.views', { count: article.views })}
                    </p>
                    {article.tags?.length > 0 && (
                      <div className="mt-1 flex gap-1">
                        {article.tags.map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={article.status === 'published' ? 'default' : 'outline'}>{article.status === 'published' ? t('dir.kb.published') : article.status === 'draft' ? t('dir.kb.draft') : article.status}</Badge>
                    <Button size="sm" variant="outline" onClick={() => handleTogglePublish(article)}>
                      {article.status === 'published' ? t('dir.kb.unpublish') : t('dir.kb.publish')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEditor(article)}>{t('agents.edit')}</Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(article.id)}>{t('common.delete')}</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === 'structure' && (
        <div className="space-y-4">
          {categories.map((cat) => (
            <Card key={cat.id}>
              <CardHeader><CardTitle>{cat.name}</CardTitle></CardHeader>
              <CardContent>
                {cat.folders.length === 0 ? <EmptyState title={t('dir.kb.noFolders')} description={t('dir.kb.noFoldersDescription')} /> : (
                  <div className="space-y-2">
                    {cat.folders.map((folder) => (
                      <div key={folder.id} className="flex items-center justify-between rounded border p-3">
                        <div>
                          <span className="font-medium">{folder.name}</span>
                          <Badge variant="outline" className="ml-2 text-xs">{folder.visibility}</Badge>
                        </div>
                        <span className="text-sm text-ink-subtle">{t('dir.kb.articlesCount', { count: folder._count.articles })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showEditor && (
        <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label={editingArticle ? t('dir.kb.editArticle') : t('dir.kb.newArticle')}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowEditor(false)} aria-hidden="true" />
          <div className="relative ml-auto w-full max-w-2xl overflow-y-auto bg-surface p-6 shadow-overlay">
            <h2 className="text-lg font-bold">{editingArticle ? t('dir.kb.editArticle') : t('dir.kb.newArticle')}</h2>
            <div className="mt-4 space-y-4">
              <Input label={t('dir.kb.fieldTitle')} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <div>
                <label htmlFor="kb-folder-select" className="mb-1 block text-sm font-medium text-ink-muted">{t('dir.kb.folder')}</label>
                <DropdownSelect
                  options={[
                    { value: '', label: t('dir.kb.selectFolder') },
                    ...categories.flatMap((cat) => cat.folders.map((f) => ({ value: f.id, label: `${cat.name} / ${f.name}` }))),
                  ]}
                  value={form.folderId}
                  onChange={(value) => setForm({ ...form, folderId: value })}
                />
              </div>
              <RichTextEditor
                label={t('dir.kb.content')}
                value={form.content}
                onChange={(html) => setForm({ ...form, content: html })}
                minHeight="300px"
                placeholder={t('dir.kb.contentPlaceholder')}
              />
              <div>
                <label htmlFor="kb-status-select" className="mb-1 block text-sm font-medium text-ink-muted">{t('dir.status')}</label>
                <DropdownSelect
                  options={[
                    { value: 'draft', label: t('dir.kb.draft') },
                    { value: 'published', label: t('dir.kb.published') },
                  ]}
                  value={form.status}
                  onChange={(value) => setForm({ ...form, status: value })}
                  searchable={false}
                />
              </div>
              <Input label={t('dir.kb.tags')} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />

              {/* Attachments — only available when editing an existing article */}
              {editingArticle && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-ink-muted">{t('dir.kb.attachments')}</label>
                  <div className="space-y-2">
                    {attachments.map((att) => (
                      <div key={att.id} className="flex items-center justify-between rounded border border-line px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 truncate">
                          <Paperclip className="h-4 w-4 shrink-0 text-ink-subtle" />
                          <span className="truncate">{att.fileName}</span>
                          <span className="shrink-0 text-xs text-ink-subtle">{formatFileSize(att.fileSize)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" title={t('dir.kb.download')} aria-label={t('dir.kb.downloadFile', { name: att.fileName })} onClick={() => handleDownloadAttachment(att.id)} className="rounded p-1 text-ink-subtle hover:bg-surface-subtle hover:text-ink-muted">
                            <Download className="h-4 w-4" />
                          </button>
                          <button type="button" title={t('dir.kb.remove')} aria-label={t('dir.kb.removeFile', { name: att.fileName })} onClick={() => handleDeleteAttachment(att.id)} className="rounded p-1 text-ink-subtle hover:bg-danger-50 hover:text-danger-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {attachments.length === 0 && <p className="text-xs text-ink-subtle">{t('dir.kb.noAttachments')}</p>}
                  </div>
                  <div className="mt-2">
                    <input ref={fileInputRef} type="file" className="hidden" onChange={handleUploadAttachment} aria-label={t('dir.kb.uploadAria')} />
                    <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                      <Paperclip className="mr-1 h-4 w-4" />
                      {uploading ? t('dir.kb.uploading') : t('dir.kb.addFile')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleSaveArticle}>{t('common.save')}</Button>
                <Button variant="outline" onClick={() => setShowEditor(false)}>{t('common.cancel')}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
